import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import type { AuthState, User } from '../types'

const TOKEN_TTL_MS = 60 * 60 * 1000 // 1시간

interface AuthContextType {
  token: string | null
  user: User | null
  login: (token: string, user: User) => void
  logout: () => void
  updateUser: (patch: Partial<User>) => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | null>(null)

const EMPTY_AUTH: AuthState = { token: null, user: null, expiresAt: null }

function loadStoredAuth(): AuthState {
  try {
    const stored = localStorage.getItem('auth')
    if (stored) {
      const parsed: AuthState = JSON.parse(stored)
      if (parsed.expiresAt && Date.now() < parsed.expiresAt) {
        return parsed
      }
      localStorage.removeItem('auth')
    }
  } catch {}
  return EMPTY_AUTH
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>(loadStoredAuth)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const logout = useCallback(() => {
    clearTimer()
    localStorage.removeItem('auth')
    setAuth(EMPTY_AUTH)
  }, [clearTimer])

  const scheduleExpiry = useCallback((expiresAt: number) => {
    clearTimer()
    const delay = expiresAt - Date.now()
    if (delay > 0) {
      timerRef.current = setTimeout(logout, delay)
    } else {
      logout()
    }
  }, [clearTimer, logout])

  const login = useCallback((token: string, user: User) => {
    const expiresAt = Date.now() + TOKEN_TTL_MS
    const state: AuthState = { token, user, expiresAt }
    localStorage.setItem('auth', JSON.stringify(state))
    setAuth(state)
    scheduleExpiry(expiresAt)
  }, [scheduleExpiry])

  const updateUser = useCallback((patch: Partial<User>) => {
    setAuth(prev => {
      if (!prev.token || !prev.user) return prev
      const updated: AuthState = { ...prev, user: { ...prev.user, ...patch } }
      localStorage.setItem('auth', JSON.stringify(updated))
      return updated
    })
  }, [])

  // 페이지 새로고침 후 남은 만료 시간으로 타이머 재설정
  useEffect(() => {
    if (auth.token && auth.expiresAt) {
      scheduleExpiry(auth.expiresAt)
    }
    return clearTimer
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{
      token: auth.token,
      user: auth.user,
      login,
      logout,
      updateUser,
      isAuthenticated: !!auth.token,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
